import React from 'react';
import PropTypes from 'prop-types';
import MUIListItem from '@material-ui/core/ListItem';
import MUIListItemText from '@material-ui/core/ListItemText';
import style from './ListItem.style';
import filterHtmlProps from '../../utils/filterHtmlProps';
import theme from '../../themes/default';
import Tooltip from '../Tooltip/Tooltip';

const ListItem = ({
  leftDecoration,
  rightDecoration,
  primaryText,
  secondaryText,
  size,
  onClick,
  component: Component,
  className,
  label,
  textTruncate,
  classes: { text: textClass, container: containerClass },
  isDraggable,
  dropType,
  isDragging,
  testID,
  highlighted,
  attributes,
  disabled,
  disableTypography,
  withTooltipInPosition,
  ...otherProps
}) => {
  const getItem = () => (
    <MUIListItemText
      disableTypography={disableTypography}
      css={[
        style.text.base,
        textTruncate && style.text.truncate,
        leftDecoration && style.text.afterDecoration,
      ]}
      primaryTypographyProps={{
        style: {
          fontFamily: theme.font.family.workSans,
          fontSize: theme.font.size.copy,
          fontWeight: String(theme.font.weight.regular),
          lineHeight: theme.font.lineHeight.regular,
        },
      }}
      className={textClass}
      primary={primaryText}
      secondary={secondaryText}
    />
  );

  return (
    // MUIListItem doesn't forward refs correctly so we have to wrap
    <Component
      id={testID}
      className={containerClass}
      {...filterHtmlProps(otherProps, ['tabIndex', 'aria-selected', 'role'])}
    >
      <MUIListItem
        component="div"
        disableRipple={isDraggable}
        disabled={disabled}
        button={!!onClick}
        aria-label={label}
        onClick={onClick}
        css={[
          style.item.base,
          style.item.bySize[size],
          highlighted && style.item.highlighted,
          dropType && style.item.draggedOverByType[dropType],
          isDragging && style.item.dragging,
        ]}
        className={className}
        {...attributes}
      >
        {leftDecoration}
        {withTooltipInPosition ? (
          <Tooltip title={primaryText} position={withTooltipInPosition}>
            {getItem()}
          </Tooltip>
        ) : (
          getItem()
        )}
        {rightDecoration}
      </MUIListItem>
    </Component>
  );
};

ListItem.defaultProps = {
  leftDecoration: null,
  rightDecoration: null,
  size: 'small',
  primaryText: '',
  secondaryText: '',
  onClick: null,
  label: undefined,
  component: 'li',
  className: undefined,
  textTruncate: false,
  classes: {},
  dropType: null,
  isDragging: false,
  isDraggable: false,
  testID: undefined,
  highlighted: false,
  disabled: false,
  disableTypography: true,
  attributes: {},
  withTooltipInPosition: undefined,
};

ListItem.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  leftDecoration: PropTypes.element,
  rightDecoration: PropTypes.element,
  primaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  secondaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClick: PropTypes.func,
  component: PropTypes.elementType,
  className: PropTypes.string,
  label: PropTypes.string,
  textTruncate: PropTypes.bool,
  classes: PropTypes.shape({
    text: PropTypes.string,
    container: PropTypes.string,
  }),
  dropType: PropTypes.oneOf(['before', 'after']),
  isDragging: PropTypes.bool,
  isDraggable: PropTypes.bool,
  testID: PropTypes.string,
  highlighted: PropTypes.bool,
  disabled: PropTypes.bool,
  disableTypography: PropTypes.bool,
  attributes: PropTypes.object,
  withTooltipInPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
};

export default React.memo(ListItem);
